import React, { useEffect, useRef, useState } from 'react';
import styles from './ChargeDetails.module.scss';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../../../app/hooks';
import { DATA_FETCH_STATUS } from '../../../../../../store/dashboard/constants';
import { fetchChargeDetails } from '../../../../../../store/dashboard/creditBureau/actions';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import { TabModule } from '../../../../../../utils/constants/commonConstants';
import { ctaPurpleRight, infoIconGrayFill } from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_VIEW_DETAILS } from '../../../../../..//utils/constants/mixpanelEvents/investorEvents';
import { setRolesForUser } from '../../../../../../utils/utils';
import { useParams } from 'react-router-dom';
import ChargeDetailsDrawer from './ChargeDrawer/ChargeDetailsDrawer';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { formatFinancialDate, formatDateByFormat } from '../../../../../../utils/dateUtils';
import AskAicaQuestionBadge from '../../../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
interface ChargeDetailsProps {
    isPDFReport: boolean;
    mcaDocApiData?: any;
}

const CHARGEDETAILS_CONSTANTS = {
    CHARGE_HOLDER_NAME: 'Charge Holder Name',
    TOTAL_OPEN_CHARGES: 'Total Open Charges',
    TOTAL_CLOSED_CHARGES: 'Total Closed Charges',
    CHARGE_AMOUNT: 'Charge Amount',
    MODIFICATION_DATE: 'Modification Date',
    CHARGE_STATUS: 'Status',
    CHARGE_DETAILS: 'Charge Details',
    PARTICULARS_OF_CHARGE: 'Particulars of the Charge',
};
const TOOLTIP_CONTENT = 'The moderation date for closed charges corresponds to the satisfaction date of the charge.';

// Keys to be excluded from Overview table
const excludedKeys = [
    'Property Type',
    'Number of Holders',
    'Instrument Description',
    'Rate of Interest',
    'Terms of Payment',
    'Property Particulars',
    'Extent and Operation',
    'Other Terms',
    'Modification Particulars',
    'Joint Holding',
    'Consortium Holding',
];

const ChargeDetails: React.FC<ChargeDetailsProps> = ({ isPDFReport = false, mcaDocApiData = {} as any }) => {
    const dispatch = useDispatch();

    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<any>(null);

    const [isLoading, setIsLoading] = useState(false);
    const userData = useAppSelector((state) => state.user.userData);
    const roleName = setRolesForUser(userData?.roles?.[0]);
    const params = useParams();

    const chargeDetails = useAppSelector((state) => state.dashboard.creditBureau.mainData.chargeDetails);
    const taskTrackerMap = useAppSelector((state) => state.investor.taskTrackerMap);
    const mcaChargesTaskTrackerData = taskTrackerMap?.['AION_INGEST_MCA_CHARGES_DATA'];

    const taskTrackerFailed = mcaChargesTaskTrackerData?.status === 'FAILED';
    const taskTrackerCompleted = mcaChargesTaskTrackerData?.status === 'COMPLETE';
    // const taskTrackerInProgress = mcaChargesTaskTrackerData?.status === 'IN_PROGRESS';

    const rowData = chargeDetails?.plots?.[0]?.rowData;
    const rowDataExists = rowData?.length > 0;
    let pollingIntervalRef: any = useRef(null);

    useEffect(() => {
        fetchData(true);
    }, []);

    useEffect(() => {
        if (modulePollingList.includes(TabModule.OVERVIEW)) {
            fetchData();
        }
    }, [modulePollingList]);

    useEffect(() => {
        if (chargeDetails?.metadata?.status === DATA_FETCH_STATUS.PROCESSING) {
            pollingIntervalRef.current = setInterval(() => {
                fetchData();
            }, 5000);
        } else {
            clearInterval(pollingIntervalRef?.current);
        }

        return () => {
            clearInterval(pollingIntervalRef?.current);
        };
    }, [chargeDetails?.metadata?.status]);

    const fetchData = (isPolling = false) => {
        if (!isPolling) setIsLoading(true);
        dispatch(
            fetchChargeDetails(
                {
                    graphNames: ['mcaCharges'], // @todo: change this hardcoding to store value in future. Since the credit beaureu config is hardcoded in Investor Saga Line No. 30.
                    tabName: 'compliances',
                    startDate: '',
                    endDate: '',
                    tenure: 'Monthly',
                },
                {
                    onSuccess: () => {
                        setIsLoading(false);
                    },
                    onError: () => {
                        setIsLoading(false);
                    },
                },
            ),
        );
    };

    const getBannerStatusColors = (status: string) => {
        let color = '';
        let banner = '';
        switch (status) {
            case 'OPEN':
                color = '#DF666A';
                banner = '#FDF0EC';
                break;
            case 'CLOSED':
                color = '#999999';
                banner = '#E7E7E7';
                break;
        }
        return {
            color,
            banner,
        };
    };

    function convertNumber(numStr) {
        let cleanedNumStr = numStr.replace(/[₹,\s]/g, '');
        let number = parseFloat(cleanedNumStr);
        if (isNaN(number)) {
            return numStr;
        }
        let result = (number / 1e7).toFixed(2) + ' Cr';
        return result;
    }
    const getSeparatorRowText = (rowIndex: number, toShowSeparator: boolean) => {
        if (rowIndex === 0) {
            return CHARGEDETAILS_CONSTANTS.CHARGE_DETAILS;
        }
        if (toShowSeparator) {
            return CHARGEDETAILS_CONSTANTS.PARTICULARS_OF_CHARGE;
        }
        return '';
    };

    const handleCTAClick = (row) => {
        setSelectedRowData(row); // Set the selected row data
        setIsDrawerOpen(true); // Open the drawer

        Mixpanel.track(AICA_VIEW_DETAILS, {
            userRole: roleName,
            pnoPlatform: userData.investorOrgId,
            userName: userData.userName,
            currentUrl: window.location.href,
            companyOrgId: params.id,
            tabName: TabModule.OVERVIEW,
            header: CHARGEDETAILS_CONSTANTS.CHARGE_DETAILS,
        });
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false); // Close the drawer
        setSelectedRowData(null); // Clear the selected row data
    };

    const ctaElement = (row) => {
        if (isPDFReport) return <></>;
        return (
            <div className={styles.Cta} onClick={() => handleCTAClick(row)}>
                <img src={ctaPurpleRight} alt="" />
                View Details
            </div>
        );
    };

    const renderHeaders = () => (
        <div className={clsx({ [styles.Wrap]: isPDFReport, [styles.ChargesHeaders]: true })}>
            {chargeDetails?.headers?.map((header, index) => (
                <div
                    key={index}
                    className={clsx({
                        [styles.AmountHeader]: header.name === CHARGEDETAILS_CONSTANTS.CHARGE_AMOUNT,
                    })}
                >
                    {header.name}
                    {header.name === CHARGEDETAILS_CONSTANTS.MODIFICATION_DATE &&
                        (isPDFReport ? (
                            <></>
                        ) : (
                            <Tooltip
                                content={TOOLTIP_CONTENT}
                                direction="bottom"
                                fitContent={true}
                                style={{ width: '300px', height: 'max-content' }}
                            >
                                <img
                                    src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/info-black.svg'}
                                    height="14px"
                                    style={{ marginLeft: '.5rem', marginBottom: '-.125rem' }}
                                    alt="info"
                                />
                            </Tooltip>
                        ))}
                </div>
            ))}
        </div>
    );

    const renderNoChargeDetails = () => (
        <div className={styles.EmptydataRow}>
            <img className={styles.InfoIcon} src={infoIconGrayFill} height={'14px'} alt="info" />
            No Charge Details found on MCA
        </div>
    );

    const renderChargeDetailRows = () => {
        return rowData?.map((row, index) => {
            const chargeHolderName = row.amounts.find((amount) => amount.xValue === CHARGEDETAILS_CONSTANTS.CHARGE_HOLDER_NAME)?.rowVal;
            const isTotalOpenCharges = chargeHolderName === CHARGEDETAILS_CONSTANTS.TOTAL_OPEN_CHARGES;
            const isTotalClosedCharges = chargeHolderName === CHARGEDETAILS_CONSTANTS.TOTAL_CLOSED_CHARGES;

            const chargeStatus = row.amounts.find((amount) => amount.xValue === CHARGEDETAILS_CONSTANTS.CHARGE_STATUS)?.rowVal;

            // Check if all excluded keys have rowVal as "-"
            const hasOnlyExcludedKeysWithDash = row.amounts
                .filter((item) => excludedKeys.includes(item.xValue)) // Consider only excluded keys
                .every((item) => item.rowVal === '-');

            // Filter amounts to only include those that have a corresponding header
            const visibleColumns = chargeDetails?.headers?.map((header) => header.name);
            const filteredAmounts = row?.amounts?.filter((rowItem) => visibleColumns.includes(rowItem.xValue));

            return (
                <div
                    className={clsx(
                        {
                            [styles.TotalsRow]: isTotalOpenCharges || isTotalClosedCharges,
                            [styles.FixHeight]: !isPDFReport,
                            [styles.ClickableRow]: chargeStatus === 'OPEN' && !hasOnlyExcludedKeysWithDash,
                        },
                        styles.ChargesRows,
                    )}
                    key={index}
                    onClick={() => chargeStatus === 'OPEN' && !hasOnlyExcludedKeysWithDash && handleCTAClick(row)} // Make the row clickable
                    style={{ cursor: chargeStatus === 'OPEN' && !hasOnlyExcludedKeysWithDash ? 'pointer' : 'default' }} // Add pointer cursor for clickable rows
                >
                    {/* Render only the filtered columns */}
                    {filteredAmounts.map((rowItem, j) => (
                        <div
                            className={clsx(
                                {
                                    [styles.Amount]: rowItem?.xValue === CHARGEDETAILS_CONSTANTS.CHARGE_AMOUNT,
                                    [styles.Total]:
                                        (isTotalOpenCharges || isTotalClosedCharges) &&
                                        (rowItem?.xValue === CHARGEDETAILS_CONSTANTS.CHARGE_HOLDER_NAME ||
                                            rowItem?.xValue === CHARGEDETAILS_CONSTANTS.CHARGE_AMOUNT),
                                    [styles.NoWrap]: !isPDFReport,
                                },
                                styles.RowItem,
                            )}
                            key={`${rowItem.rowVal}-${j}`}
                            style={
                                rowItem.xValue === CHARGEDETAILS_CONSTANTS.CHARGE_STATUS
                                    ? {
                                          padding: '0.25rem 0.75rem',
                                          borderRadius: '0.5rem',
                                          width: 'fit-content',
                                          background: getBannerStatusColors(rowItem.rowVal).banner,
                                          color: getBannerStatusColors(rowItem.rowVal).color,
                                          transform: isPDFReport ? 'scale(0.8)' : '',
                                      }
                                    : {
                                          textOverflow: isPDFReport ? 'clip' : 'ellipsis',
                                      }
                            }
                            title={rowItem.rowVal}
                        >
                            {rowItem.rowVal.startsWith('₹') ? convertNumber(rowItem.rowVal) : rowItem.rowVal}
                        </div>
                    ))}

                    {/* Add CTA only if the charge status is "OPEN" and not all values are "-" */}
                    {chargeStatus === 'OPEN' && !hasOnlyExcludedKeysWithDash && <div className={styles.RowItem}>{ctaElement(row)}</div>}
                </div>
            );
        });
    };

    const render = (
        <div className={styles.chargeDetails}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.heading}>Charge Details</h2>
                    {mcaDocApiData &&
                        mcaDocApiData?.reportStatus === DATA_FETCH_STATUS.PROCESSING &&
                        mcaDocApiData?.isFetching === true && (
                            <div className={styles.loaderMessage}>
                                <LoadingSpinner color="#6021b3" height="15px" />
                                <span>Fetching latest data from MCA (takes upto 24 hours)</span>
                            </div>
                        )}
                </div>
                <div className={styles.headerRight}>
                    {!isPDFReport && rowDataExists && !taskTrackerFailed && (
                        <AskAicaQuestionBadge question="Summarize the charge details" prompt="Summarize the MCA charge details" />
                    )}
                    {/* Show Last Updated At from taskTracker dateCreated */}
                    {mcaChargesTaskTrackerData?.dateCreated && (
                        <span className={styles.date}>
                            {`Last updated on ${formatDateByFormat(mcaChargesTaskTrackerData.dateCreated, 'D MMM YYYY')}`}
                        </span>
                    )}
                    {/* Always show Data Source */}
                    <div className={styles.dataSource}>
                        Data Source: <span className={styles.dataSourceText}>MCA</span>
                    </div>
                    {!rowDataExists && taskTrackerFailed && <div className={styles.errorMessage}>Not Able to Fetch</div>}
                </div>
            </div>

            {(rowDataExists || taskTrackerCompleted) && (
                <div className={styles.content}>
                    <div className={styles.contentBody}>
                        <div className={styles.table} style={{ ...(isPDFReport && { fontSize: '11px' }) }}>
                            {renderHeaders()}
                            {rowDataExists ? renderChargeDetailRows() : renderNoChargeDetails()}
                        </div>
                    </div>
                </div>
            )}

            <ChargeDetailsDrawer
                isDrawerOpen={isDrawerOpen}
                onClose={closeDrawer}
                selectedRowData={selectedRowData}
                getSeparatorRowText={getSeparatorRowText}
                getBannerStatusColors={getBannerStatusColors}
                convertNumber={convertNumber}
            />
        </div>
    );
    if (isPDFReport)
        return rowData?.length && reportSectionsToShow.includes(REPORT_SECTION_NAMES.CHARGE_DETAILS) ? (
            <A4Page sectionName={REPORT_SECTION_NAMES.CHARGE_DETAILS}>{render}</A4Page>
        ) : (
            <></>
        );
    else return render;
};
export default ChargeDetails;
